import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";

import "./index.scss";

import { ConfigContainer } from "components";
import { Title, Container, Text } from "components/Form";
import { analysisGroups, createMap } from "state_management";
import AnalysisTaskBundleSetup from "./AnalysisTaskBundleSetup";
import useT from "utils/hooks/useT"

function getDefaultBundleSelection(bundle) {
  return {
    analysisTasks: [],
    cdmo: Boolean(bundle?.cdmo),
  }
}

export default function AnalysisGroupConfig({
  analysisGroup,
  isBundleSettings,
  onClose,
  onSubmit
}) {
  const dispatch = useDispatch();
  const { t } = useT("common")

  const rawAnalysisDefinitions = useSelector((state) => state.analysisDefinitions);
  const analysisGroupId = analysisGroup ? analysisGroup.id : null;
  const bundles = useSelector((state) => state.analysisGroups.bundles[analysisGroupId]);
  const rawAnalysisTasks = useSelector((state) => state.analysisGroups.tasks);

  const analysisDefinitions = useMemo(() => rawAnalysisDefinitions || {}, [rawAnalysisDefinitions]);
  const analysisTasks = useMemo(() => rawAnalysisTasks || {}, [rawAnalysisTasks]);

  const analysisGroupBundles = useMemo(() => bundles || {}, [bundles]);

  const groupedAnalysisIds = useMemo(() =>
    Object.keys(analysisDefinitions).filter((id) =>
      analysisDefinitions[id].analysisGroupIds?.includes(analysisGroupId)
    ),
    [analysisDefinitions, analysisGroupId]
  );

  const allTaskDefinitions = useMemo(() =>
    groupedAnalysisIds.flatMap(
      (id) => analysisDefinitions[id].taskDefinitions || []
    ),
    [groupedAnalysisIds, analysisDefinitions]
  );

  const tasks = useMemo(() => createMap(allTaskDefinitions), [allTaskDefinitions]);

  const [name, setName] = useState(analysisGroup ? analysisGroup.name : "");

  const initialBundleSelections = useMemo(() => {
    return Object.values(analysisGroupBundles).reduce((acc, bundle) => {
      const tasksForThisBundle = Object.values(analysisTasks).filter(
        (t) => t.bundleId === bundle.id
      );
      acc[bundle.id] = {
        analysisTasks: tasksForThisBundle.map((t) => t.id),
        cdmo: Boolean(bundle.cdmo),
      };
      return acc;
    }, {});
  }, [analysisGroupBundles, analysisTasks]);

  const [bundleSelectionsById, setBundleSelectionsById] = useState({});

  useEffect(() => {
    setBundleSelectionsById((prev) => {
        const merged = { ...initialBundleSelections };

        // Preserve any user edits for existing bundles.
        Object.keys(prev).forEach((bundleId) => {
          if (bundleId in merged) {
            merged[bundleId] = {
              ...merged[bundleId],
              ...prev[bundleId],
            };
          }
        });
        return merged;
      })
  }, [initialBundleSelections]);

  const addNewBundle = () => {
    const newBundleId = uuidv4();
    const highestIndex = Object.values(analysisGroupBundles).reduce(
      (max, bundle) => Math.max(max, bundle.index || 0),
      0
    );
    const newIndex = highestIndex + 1;
    dispatch(analysisGroups.addBundle(analysisGroup.id, newBundleId, newIndex));
    setBundleSelectionsById((prev) => ({
      ...prev,
      [newBundleId]: getDefaultBundleSelection(),
    }));
  };

  const removeBundleFromGroup = (bundleId) => {
    setBundleSelectionsById((prev) => {
      const newSelections = { ...prev };
      delete newSelections[bundleId];
      return newSelections;
    });

    if (analysisGroup) {
      dispatch(analysisGroups.removeBundle(analysisGroup.id, bundleId));
    }
  };

  const submitHandler = () => {
    const trimmedName = name.trim();
    setName(trimmedName);
    const res = {
      name: trimmedName,
    };
    if (isBundleSettings) {
      res.selectedTasks = Object.values(analysisGroupBundles)
        .sort((a, b) => (a.index || 0) - (b.index || 0))
        .map((bundle) => {
          const selection = bundleSelectionsById[bundle.id] || getDefaultBundleSelection(bundle);
          return {
            id: bundle.id,
            analysisTasks: Array.isArray(selection.analysisTasks) ? selection.analysisTasks : [],
            cdmo: Boolean(selection.cdmo),
          }
        });
    }
    if (analysisGroup) {
      dispatch(analysisGroups.edit(analysisGroup.id, res)).then(onSubmit);
    } else {
      dispatch(analysisGroups.create(res)).then(onSubmit);
    }
  };

  const getTasksForBundle = (bundleId) => {
    const tasksUsedElsewhere = new Set();

    for (const [otherBundleId, bundleSelection] of Object.entries(bundleSelectionsById)) {
      const taskIds = bundleSelection?.analysisTasks
      if (otherBundleId !== bundleId && Array.isArray(taskIds)) {
        taskIds.forEach((tid) => tasksUsedElsewhere.add(tid));
      }
    }

    return Object.values(tasks).filter((task) => {
      // Task is explicitly assigned to this bundle
      if (task.bundleId === bundleId) return true;

      // Task is assigned to another bundle, exclude it
      if (task.bundleId && task.bundleId !== bundleId) return false;

      // Unassigned task - include only if not used elsewhere
      return !tasksUsedElsewhere.has(task.id);
    });
  };

  return (
    <ConfigContainer
      title={analysisGroup ? t("editAnalysisGroup") : t("addAnalysisGroup")}
      submitHandler={submitHandler}
      cancelHandler={onClose}
      deleteHandler={
        analysisGroup ? () => dispatch(analysisGroups.remove(analysisGroup.id)).then(onClose) : null
      }
    >
      <Container>
        <Title>{t("analysisGroupName")}</Title>
        <Text value={name} required onChange={setName} />
      </Container>

      {Object.values(analysisGroupBundles).map((bundle) => {
        const tasksForThisBundle = getTasksForBundle(bundle.id);
        const tasksMapForThisBundle = createMap(tasksForThisBundle);
        const bundleSelection = bundleSelectionsById[bundle.id] || getDefaultBundleSelection(bundle);
        const defaultSelected = bundleSelection.analysisTasks || [];
        return (
          <AnalysisTaskBundleSetup
            key={bundle.id}
            onDelete={() => removeBundleFromGroup(bundle.id)}
            index={bundle.index}
            tasks={tasksMapForThisBundle}
            cdmo={Boolean(bundleSelection.cdmo)}
            selectedTasks={defaultSelected}
            onSelectedTasksChange={(newSelectedTasks) =>
              setBundleSelectionsById((prev) => ({
                ...prev,
                [bundle.id]: {
                  ...(prev[bundle.id] || getDefaultBundleSelection(bundle)),
                  analysisTasks: Array.isArray(newSelectedTasks) ? newSelectedTasks : [],
                },
              }))
            }
            onCdmoChange={(isCdmo) =>
              setBundleSelectionsById((prev) => ({
                ...prev,
                [bundle.id]: {
                  ...(prev[bundle.id] || getDefaultBundleSelection(bundle)),
                  cdmo: Boolean(isCdmo),
                },
              }))
            }
          />
        );
      })}

      {isBundleSettings && (
        <div className="add-new-bundle">
          <p>{t("newAnalysisTaskBundle")}</p>
          <Button title={t("addNewBundle")} onClick={addNewBundle}>
            <FiPlus />
          </Button>
        </div>
      )}
    </ConfigContainer>
  );
}
